// src/services/remainingModuleConnectors.ts
// ─────────────────────────────────────────────────────────────────
// Remaining Module Connectors — Phase 5
//
// Completes the full 14-module connection audit from the Unification
// Review. Combined with whsNotificationConnectors.ts (Phase 4), all
// platform modules that produce field-relevant events now write to
// the notification bus.
//
//   SOURCE MODULE            FUNCTION                  ANCHOR
//   ──────────────────────────────────────────────────────────────
//   Travel.tsx               fireAccommodationAlert    handleAssign (line 1369)
//   Timesheets.tsx           fireTimesheetApprovedAlert approveTS (line 1311)
//   Environmental.tsx        fireEnvironmentalAlert    onSubmit callback (line 303)
//   PurchaseOrders/GRNPanel  fireGRNMaterialReadyAlert GRNRecorderModal onComplete
//   ITP.tsx                  fireHoldPointReleaseAlert releaseHoldPoint (line 428)
//   ProgressPaymentClaims    fireMilestoneAlert        (optional) certify callback
//
// At beta: these functions call POST /api/notifications/crew
// ─────────────────────────────────────────────────────────────────

import { addCrewNotification } from '../utils/mockDataNotificationBus'
import { MOCK_ROSTER_NOTIFICATIONS } from '../utils/mockDataMobile'
import type {
  AccommodationPayload, TimesheetApprovedPayload, MaterialReadyPayload,
  ITPReleasePayload,
} from '../types/mobileCrewTypes'

// Helper — get on-site crew IDs for a job
function onSiteFor(jobId: string): string[] {
  return MOCK_ROSTER_NOTIFICATIONS
    .filter(n => n.jobId === jobId && (n.status === 'ON_SITE' || n.status === 'CONFIRMED'))
    .map(n => n.employeeId)
}

// ─────────────────────────────────────────────────────────────────
// 5B-01  TRAVEL → ACCOMMODATION notification
// ─────────────────────────────────────────────────────────────────
// ADD TO Travel.tsx — handleAssign at line 1369:
//
//   import { fireAccommodationAlert } from '../services/remainingModuleConnectors'
//
//   const handleAssign = (acc: EnhancedAccommodation, empId: string) => {
//     const emp = MOCK_EMPLOYEES.find(e => e.id === empId)!
//     const now = new Date().toISOString()
//     setEnhancedAcc(prev => prev.map(a => a.id !== acc.id ? a : {
//       ...a, assignedEmployees: [...a.assignedEmployees, { employeeId: empId,
//         employeeName: emp.fullName, assignedAt: now, assignedByName: user?.fullName ?? 'PM',
//         notifiedAt: now }],
//     }))
//     fireAccommodationAlert(acc, empId, emp.fullName)   // ← ADD THIS LINE
//   }
//
// Also call when reassigning (handleResend): fireAccommodationAlert(acc, empId, emp.fullName)

export function fireAccommodationAlert(
  acc: {
    propertyName: string; address: string
    keyholderName: string; keyholderPhone: string; keyholderRole?: string
    checkIn: string; checkOut: string
    jobId?: string; jobNumber?: string
    assignedEmployees: Array<{ employeeId: string; employeeName: string }>
  },
  newEmployeeId:   string,
  newEmployeeName: string,
): void {
  const coOccupants = acc.assignedEmployees
    .filter(e => e.employeeId !== newEmployeeId)
    .map(e => e.employeeName)

  const jobIds = acc.jobId ? [acc.jobId] : []

  addCrewNotification({
    type:           'ACCOMMODATION',
    priority:       'HIGH',
    employeeIds:    [newEmployeeId],
    jobIds,
    heading:        `Accommodation assigned — ${acc.propertyName}`,
    body:           `Your accommodation for ${acc.jobNumber ?? 'your upcoming rotation'} has been booked at ${acc.propertyName}. Check-in ${acc.checkIn} — check-out ${acc.checkOut}.`,
    requiredAction: 'Acknowledge receipt. Contact keyholder on arrival if after-hours.',
    sourceModule:   'Travel.tsx',
    requiresAck:    true,
    payload: {
      propertyName:   acc.propertyName,
      address:        acc.address,
      keyholderName:  acc.keyholderName,
      keyholderPhone: acc.keyholderPhone,
      checkIn:        acc.checkIn,
      checkOut:       acc.checkOut,
      coOccupants,
      amenities:      [],
      bookingRef:     '',
    } as AccommodationPayload,
  })
}

// ─────────────────────────────────────────────────────────────────
// 5B-03  TIMESHEETS → TIMESHEET_APPROVED notification
// ─────────────────────────────────────────────────────────────────
// ADD TO Timesheets.tsx — approveTS at line 1311:
//
//   import { fireTimesheetApprovedAlert } from '../services/remainingModuleConnectors'
//
//   const approveTS = (id: string) => {
//     const ts = timesheets.find(t => t.id === id)             // ← ADD: find before update
//     setTimesheets(prev => prev.map(t => t.id === id ? { ...t, status: 'APPROVED',
//       approvedById: 'u2', approvedAt: new Date().toISOString() } : t))
//     if (ts) fireTimesheetApprovedAlert(ts, user?.fullName ?? 'PM')  // ← ADD THIS LINE
//   }

export function fireTimesheetApprovedAlert(
  ts: {
    id:             string
    employeeId:     string
    employeeName:   string
    weekStarting:   string
    entries: Array<{
      start?: string; end?: string; break?: number
      shiftType?: string; entryType?: string; adjustedByPM?: boolean
    }>
  },
  approvedByName: string,
): void {
  // Calculate totals from entries
  let ordHrs = 0, otHrs = 0, nightHrs = 0
  let adjustmentsMade = false

  for (const e of ts.entries) {
    if (!e.start || !e.end) continue
    if (e.entryType && e.entryType !== 'SITE_WORK') continue
    const startH = parseInt(e.start.split(':')[0]) + parseInt(e.start.split(':')[1]) / 60
    const endH   = parseInt(e.end.split(':')[0])   + parseInt(e.end.split(':')[1]) / 60
    const hrs    = Math.max(0, endH - startH - (e.break ?? 0.5))
    const ord    = Math.min(hrs, 8)
    const ot     = Math.max(0, hrs - 8)
    ordHrs += ord
    otHrs  += ot
    if (e.shiftType === 'NIGHT') nightHrs += ord
    if (e.adjustedByPM) adjustmentsMade = true
  }

  const payPeriodEnd = new Date(ts.weekStarting)
  payPeriodEnd.setDate(payPeriodEnd.getDate() + 6)

  addCrewNotification({
    type:        'TIMESHEET_APPROVED',
    priority:    'LOW',
    employeeIds: [ts.employeeId],
    jobIds:      [],
    heading:     `Timesheet approved — week ending ${payPeriodEnd.toLocaleDateString('en-AU')}`,
    body:        `Your timesheet for the week ending ${payPeriodEnd.toLocaleDateString('en-AU')} has been approved by ${approvedByName}. ${Math.round((ordHrs + otHrs) * 10) / 10} hours approved${adjustmentsMade ? ' (includes PM adjustments)' : ''}.`,
    sourceModule:'Timesheets.tsx',
    sourceRecordId: ts.id,
    requiresAck: false,
    payload: {
      payPeriodLabel: `Week ending ${payPeriodEnd.toLocaleDateString('en-AU')}`,
      payPeriodStart:  ts.weekStarting,
      payPeriodEnd:    payPeriodEnd.toISOString().slice(0, 10),
      ordinaryHours:   Math.round(ordHrs * 10) / 10,
      overtimeHours:   Math.round(otHrs * 10) / 10,
      nightLoadingHours: Math.round(nightHrs * 10) / 10,
      grossEstimate:   Math.round((ordHrs * 38 + otHrs * 38 * 1.5) * 100) / 100,
      adjustmentsMade,
      adjustmentReason: adjustmentsMade ? 'Adjusted by PM — see timesheet detail for breakdown' : undefined,
    } as TimesheetApprovedPayload,
  })
}

// ─────────────────────────────────────────────────────────────────
// 5B-05  ENVIRONMENTAL → ENVIRONMENTAL_ALERT notification
// ─────────────────────────────────────────────────────────────────
// ADD TO Environmental.tsx — onSubmit callback at line 303:
//
//   import { fireEnvironmentalAlert } from '../services/remainingModuleConnectors'
//
//   onSubmit={inc => {
//     setEnvIncidents(prev => [inc, ...prev])
//     setShowSpillReport(false)
//     fireEnvironmentalAlert(inc)    // ← ADD THIS LINE
//   }}

export function fireEnvironmentalAlert(incident: {
  id:               string
  type:             string      // e.g. 'SPILL', 'EMISSION', 'WASTE'
  jobId:            string
  jobNumber?:       string
  location:         string
  description:      string
  immediateActions: string
  quantityLitres?:  number
  severity?:        string
}): void {
  const crewIds = onSiteFor(incident.jobId)
  if (!crewIds.length) return

  addCrewNotification({
    type:           'ENVIRONMENTAL_ALERT',
    priority:       'HIGH',
    employeeIds:    crewIds,
    jobIds:         [incident.jobId],
    heading:        `Environmental alert — ${incident.type} reported at ${incident.jobNumber ?? incident.jobId}`,
    body:           incident.description.slice(0, 300),
    requiredAction: incident.immediateActions
      ? `Immediate containment required: ${incident.immediateActions}`
      : 'Follow site emergency response plan. Report to PM and Site Supervisor immediately.',
    sourceModule:   'Environmental.tsx',
    sourceRecordId: incident.id,
    requiresAck:    true,
    payload: {
      type:             incident.type,
      location:         incident.location,
      description:      incident.description,
      immediateActions: incident.immediateActions,
      quantityLitres:   incident.quantityLitres,
    },
  })
}

// ─────────────────────────────────────────────────────────────────
// 5B-07  PURCHASE ORDERS / GRN → MATERIAL_READY notification
// ─────────────────────────────────────────────────────────────────
// ADD TO PurchaseOrders.tsx — GRNRecorderModal onComplete callback:
//
//   import { fireGRNMaterialReadyAlert } from '../services/remainingModuleConnectors'
//
//   {grnPO && (
//     <GRNRecorderModal
//       po={grnPO}
//       onClose={() => setGrnPO(null)}
//       onComplete={grn => {                         // ← if this callback exists
//         setGrnPO(null)
//         fireGRNMaterialReadyAlert(grn, grnPO)      // ← ADD THIS LINE
//       }}
//     />
//   )}
//
// If GRNRecorderModal does not expose an onComplete prop, add the call
// directly inside addDeliveryConfirmation() in mockDataGRN.ts after the
// _bomStockOverrides.set(...) calls:
//   import { fireGRNNotification } from '../services/remainingModuleConnectors'
//   fireGRNNotification(c)

export function fireGRNMaterialReadyAlert(
  grn: {
    poRef:       string
    jobId:       string
    jobNumber:   string
    supplierName:string
    lines: Array<{
      description:      string
      qtyReceived:      number
      unit?:            string
      bomLineId?:       string | null
      deliveryLocation?:string
      status?:          string
    }>
    confirmedByName: string
  },
  po?: { description?: string },
): void {
  const crewIds   = onSiteFor(grn.jobId)
  const onSiteLines = grn.lines.filter(l => l.deliveryLocation === 'ON_SITE' && l.status !== 'REJECTED')

  if (!crewIds.length || !onSiteLines.length) return

  // Fire one notification per BOM line that's now available
  for (const line of onSiteLines.slice(0, 3)) {   // cap at 3 to avoid notification spam
    addCrewNotification({
      type:           'MATERIAL_READY',
      priority:       'MEDIUM',
      employeeIds:    crewIds,
      jobIds:         [grn.jobId],
      heading:        `Materials on site — ${line.description.slice(0, 60)}`,
      body:           `${line.description} (× ${line.qtyReceived}${line.unit ? ' ' + line.unit : ''}) has been received and confirmed on site at ${grn.jobNumber} by ${grn.confirmedByName} from ${grn.supplierName}.`,
      requiredAction: undefined,
      sourceModule:   'PurchaseOrders.tsx (GRN confirmation)',
      sourceRecordId: `${grn.poRef}:${line.bomLineId ?? line.description}`,
      requiresAck:    false,
      payload: {
        bomLineId:          line.bomLineId ?? '',
        materialDescription:line.description,
        quantity:           line.qtyReceived,
        unit:               line.unit ?? 'EA',
        stockStatus:        'ON_SITE',
      } as MaterialReadyPayload,
    })
  }

  // If more than 3 lines, fire a summary notification
  if (onSiteLines.length > 3) {
    addCrewNotification({
      type:        'MATERIAL_READY',
      priority:    'LOW',
      employeeIds: crewIds,
      jobIds:      [grn.jobId],
      heading:     `${onSiteLines.length} material lines confirmed on site — ${grn.jobNumber}`,
      body:        `PO ${grn.poRef} from ${grn.supplierName} received by ${grn.confirmedByName}. ${onSiteLines.length} items confirmed on site.`,
      sourceModule:'PurchaseOrders.tsx (GRN summary)',
      sourceRecordId: grn.poRef,
      requiresAck: false,
      payload:     { bomLineId:'', materialDescription:`${onSiteLines.length} items`, quantity:onSiteLines.length, unit:'lots', stockStatus:'ON_SITE' } as MaterialReadyPayload,
    })
  }
}

// ─────────────────────────────────────────────────────────────────
// 5B-08  ITP → ITP_RELEASE notification
// ─────────────────────────────────────────────────────────────────
// ADD TO ITP.tsx — releaseHoldPoint at line 428:
//
//   import { fireHoldPointReleaseAlert } from '../services/remainingModuleConnectors'
//
//   const releaseHoldPoint = (itemId: string) => {
//     const { user } = useAuthStore.getState()
//     const item = itp.items.find(i => i.id === itemId)    // ← ADD: find before update
//     setItp(prev => ({ ...prev, items: prev.items.map(i => i.id !== itemId ? i : {
//       ...i, holdPointReleased: true,
//       releasedBy: user?.fullName ?? 'PM', releasedAt: new Date().toISOString(),
//     }) }))
//     if (item) fireHoldPointReleaseAlert(item, itp.jobId, itp.jobNumber ?? '', user?.fullName ?? 'PM')
//   }

export function fireHoldPointReleaseAlert(
  item: {
    id:               string
    itemNumber:       string
    activity:         string
    inspectionType:   string
    holdPointReleased?:boolean
  },
  jobId:     string,
  jobNumber: string,
  releasedBy:string,
): void {
  if (item.inspectionType !== 'HOLD_POINT') return

  const crewIds = onSiteFor(jobId)
  if (!crewIds.length) return

  const now = new Date().toISOString()

  addCrewNotification({
    type:           'ITP_RELEASE',
    priority:       'HIGH',
    employeeIds:    crewIds,
    jobIds:         [jobId],
    heading:        `Hold point released — ${item.itemNumber} — ${jobNumber}`,
    body:           `ITP hold point ${item.itemNumber} (${item.activity}) has been released by ${releasedBy}. Work may now proceed beyond this point.`,
    requiredAction: 'Confirm you have sighted the hold point release before proceeding. Ensure any required test records have been completed.',
    sourceModule:   'ITP.tsx',
    sourceRecordId: item.id,
    requiresAck:    true,
    payload: {
      holdPointRef:        item.itemNumber,
      holdPointDescription:item.activity,
      activityReleased:    `Work beyond ${item.itemNumber} — ${item.activity}`,
      releasedBy,
      releasedAt:          now,
    } as ITPReleasePayload,
  })
}

// ─────────────────────────────────────────────────────────────────
// 5B-06  PROGRESS CLAIMS → PROGRESS_MILESTONE (optional)
// ─────────────────────────────────────────────────────────────────
// ADD TO ProgressPaymentClaims.tsx — milestone certification callback:
//
//   import { fireMilestoneAlert } from '../services/remainingModuleConnectors'
//
//   // When a milestone is certified (find the certification handler and add):
//   fireMilestoneAlert(milestone, jobNumber)

export function fireMilestoneAlert(
  milestone: { id: string; title: string; milestoneAmount?: number },
  jobId:       string,
  jobNumber:   string,
  certifiedBy: string,
): void {
  const crewIds = onSiteFor(jobId)
  if (!crewIds.length) return

  addCrewNotification({
    type:        'PROGRESS_MILESTONE',
    priority:    'LOW',
    employeeIds: crewIds,
    jobIds:      [jobId],
    heading:     `Milestone certified — ${milestone.title} — ${jobNumber}`,
    body:        `Project milestone "${milestone.title}" has been certified by ${certifiedBy} on ${jobNumber}. Great work team.${milestone.milestoneAmount ? ` Claim value: $${milestone.milestoneAmount.toLocaleString()}.` : ''}`,
    sourceModule:'ProgressPaymentClaims.tsx',
    sourceRecordId: milestone.id,
    requiresAck: false,
    payload:     { milestoneTitle: milestone.title, jobNumber, certifiedBy },
  })
}

// ─────────────────────────────────────────────────────────────────
// MODULE CONNECTION AUDIT — after Phase 5
// ─────────────────────────────────────────────────────────────────
// All 14 modules from the Unification Review:
//
//  1  Crew Roster (rosterService)         ✅ Phase 1 (existing)
//  2  WHS Credentials (credentialView)    ✅ Phase 1 (existing, enhanced P4)
//  3  Tasks & Photos (taskService)        ✅ Phase 1 (existing, enhanced P3)
//  4  WHS Inbox                           ✅ Phase 1 (existing, enhanced P4)
//  5  WHSO Alerts                         ✅ Phase 1 (existing, enhanced P4)
//  6  Travel / Accommodation              ✅ Phase 5 — fireAccommodationAlert
//  7  Incidents (auto-alert)              ✅ Phase 4 — fireIncidentAlert
//  8  SWMS (revision alert)               ✅ Phase 4 — fireSWMSRevisionAlert
//  9  Permits to Work                     ✅ Phase 4 — firePermitApprovalAlert
// 10  Toolbox Talks (sign-off)            ✅ Phase 4 — fireToolboxSignoffRequest
// 11  Plant & Equipment (defects)         ✅ Phase 4 — firePlantDefectAlert
// 12  Purchase Orders / GRN               ✅ Phase 5 — fireGRNMaterialReadyAlert
// 13  ITP / Hold Points                   ✅ Phase 5 — fireHoldPointReleaseAlert
// 14  Work Instructions → ITP             ✅ Phase 3 — taskService.recordStepCompletion
// 15  Timesheets (approval)               ✅ Phase 5 — fireTimesheetApprovedAlert
// 16  Environmental                       ✅ Phase 5 — fireEnvironmentalAlert
// 17  Progress Claims (optional)          ✅ Phase 5 — fireMilestoneAlert
